import { CalendarDays, Filter, List, MoreHorizontal, Plus, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Avatar, Switch, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { AppointmentDrawer, type DrawerMode } from "@/features/appointment-drawer";
import { AppointmentList } from "@/features/appointment-list";
import { NewAppointmentDialog, type NewTab } from "@/features/appointment-new";
import { BlockDetailsDialog } from "@/features/block-details";
import { CalendarBoard } from "@/features/calendar-board";
import { CallbackDetails } from "@/features/callback-details";
import { ManageView, NO_FILTERS, filterCount, type ViewFilters } from "@/features/manage-view";
import { ApiError } from "@/lib/api";
import type { PickedTime } from "@/lib/calendar-view";
import { useAuth } from "@/lib/auth";
import { TYPE_INFO, minutesText, staffName } from "@/lib/calendars";
import { useCalendars, useDeleteCalendar, useMainCalendar, useUpdateCalendar } from "@/lib/queries";
import { atLeast, type Calendar, type CalendarBlock, type CalendarCallback } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Calendars (ACL-252, ADR-085 in `platform`): the sidebar's page for appointments and the
 * calendars they are booked on, in GoHighLevel's tabs. The calendar view (Day, Week, Month) and
 * the appointment list share one set of filters — Manage view — so they always show the same
 * callbacks and appointments; New books either or blocks off time. Everyone signed in works here, a rep with their
 * own appointments; the calendar settings are a supervisor's to read and an admin's to change.
 *
 * The tab and the open appointment are in the URL (`?tab=list&appointment=12`).
 */
export function CalendarsPage() {
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab = requested === "list" || (requested === "settings" && supervisor) ? requested : "calendar";
  const openId = Number(params.get("appointment")) || null;
  const [drawerMode, setDrawerMode] = React.useState<DrawerMode>("view");
  const [filters, setFilters] = React.useState<ViewFilters>(NO_FILTERS);
  const [managing, setManaging] = React.useState(true);
  const [booking, setBooking] = React.useState<NewTab | null>(null);
  const [picked, setPicked] = React.useState<PickedTime | null>(null);
  // New opens on a callback, the booking a rep makes most (ACL-659).
  const openNew = (at: PickedTime | null = null, kind: NewTab = "callback") => {
    setPicked(at);
    setBooking(kind);
  };
  const [block, setBlock] = React.useState<CalendarBlock | null>(null);
  const [callback, setCallback] = React.useState<CalendarCallback | null>(null);

  const setParam = (key: string, value: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  const openAppointment = (id: number | null, mode: DrawerMode = "view") => {
    setDrawerMode(mode);
    setParam("appointment", id === null ? null : String(id));
  };

  const viewing = tab !== "settings";
  const count = filterCount(filters, tab === "list");

  return (
    <Page className="max-w-none">
      <PageHeader
        title="Calendars"
        action={
          viewing && (
            <>
              <Button variant={managing ? "subtle" : "outline"} onClick={() => setManaging(!managing)}>
                <Filter />
                Manage view{count > 0 ? ` (${count})` : ""}
              </Button>
              <Button onClick={() => openNew()}>
                <Plus />
                New
              </Button>
            </>
          )
        }
      />
      <Tabs value={tab} onValueChange={(next) => setParam("tab", next === "calendar" ? null : next)}>
        <TabsList>
          <TabsTrigger value="calendar">
            <CalendarDays aria-hidden />
            Calendar view
          </TabsTrigger>
          <TabsTrigger value="list">
            <List aria-hidden />
            Appointment list view
          </TabsTrigger>
          {supervisor && (
            <TabsTrigger value="settings">
              <Settings2 aria-hidden />
              Calendar settings
            </TabsTrigger>
          )}
        </TabsList>
        {viewing && (
          <div className="flex flex-col gap-4 pt-4 lg:flex-row-reverse lg:items-start">
            {managing && (
              <ManageView filters={filters} onChange={setFilters} onClose={() => setManaging(false)} withUsers={supervisor} withDates={tab === "list"} />
            )}
            <div className="min-w-0 flex-1">
              <TabsContent value="calendar">
                <CalendarBoard
                  filters={filters}
                  onOpenAppointment={(id) => openAppointment(id)}
                  onOpenBlock={setBlock}
                  onOpenCallback={setCallback}
                  onPick={openNew}
                />
              </TabsContent>
              <TabsContent value="list">
                <AppointmentList
                  filters={filters}
                  onOpen={openAppointment}
                  onOpenCallback={setCallback}
                  onNew={() => openNew(null, "appointment")}
                />
              </TabsContent>
            </div>
          </div>
        )}
        {supervisor && (
          <TabsContent value="settings" className="pt-4">
            <CalendarSettings />
          </TabsContent>
        )}
      </Tabs>

      <AppointmentDrawer
        appointmentId={openId}
        startIn={drawerMode}
        onOpenChange={(isOpen) => !isOpen && openAppointment(null)}
        onOpenAppointment={(id) => openAppointment(id)}
      />
      <NewAppointmentDialog
        open={booking !== null}
        tab={booking ?? "appointment"}
        picked={picked}
        onOpenChange={(isOpen) => !isOpen && setBooking(null)}
        onBooked={(id) => openAppointment(id)}
      />
      <BlockDetailsDialog block={block} onClose={() => setBlock(null)} />
      <CallbackDetails callback={callback} onClose={() => setCallback(null)} />
    </Page>
  );
}

function CalendarSettings() {
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const navigate = useNavigate();
  const toast = useToast();
  // Asked for first, so the main calendar is on the list even before anyone has booked (ADR-215).
  const main = useMainCalendar();
  const { data, isPending, error, refetch } = useCalendars({}, { enabled: !main.isPending });
  const update = useUpdateCalendar();
  const remove = useDeleteCalendar();
  const [deleting, setDeleting] = React.useState<Calendar | null>(null);

  const confirmDelete = (c: Calendar) =>
    remove.mutate(c.id, {
      onSuccess: () => {
        setDeleting(null);
        toast.success("Calendar deleted", c.name);
      },
      onError: (e) => {
        setDeleting(null);
        toast.error("Could not delete the calendar", e instanceof ApiError ? e.message : "Please try again.");
      },
    });

  const setActive = (c: Calendar, active: boolean) =>
    update.mutate(
      { id: c.id, active },
      {
        onSuccess: () =>
          toast.success(
            active ? "Calendar on" : "Calendar off",
            active ? `${c.name} takes bookings.` : `${c.name} takes no new bookings; its appointments stay.`,
          ),
        onError: (e) => toast.error("Could not change the calendar", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  // The main calendar first; any older ones after it, as they were.
  const calendars = [...(data ?? [])].sort((a, b) => Number(b.is_main) - Number(a.is_main));

  return (
    <>
      <p className="pb-3 text-sm text-text-muted">
        Every appointment is booked on the main calendar. Every active rep is on it: to stop booking a rep, give them no
        hours.
      </p>
      <Card>
        {isPending ? (
          <TableSkeleton rows={4} cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load calendars"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : calendars.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No calendars yet" description="The main calendar could not be opened." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Calendar</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Meeting</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {calendars.map((c) => (
                <TableRow key={c.id} className="[&>td]:h-12">
                  <TableCell>
                    <Link
                      to={`/calendars/settings/${c.id}`}
                      className={cn("font-medium hover:underline", c.active ? "text-text" : "text-text-muted")}
                    >
                      {c.name}
                    </Link>
                    {c.is_main && (
                      <Badge tone="success" size="sm" className="ml-2">
                        Main
                      </Badge>
                    )}
                    {c.description && <p className="max-w-md truncate text-xs text-text-muted">{c.description}</p>}
                  </TableCell>
                  <TableCell>
                    <Badge tone="accent" size="sm">
                      {TYPE_INFO[c.calendar_type].label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StaffStack calendar={c} />
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {minutesText(c.duration_minutes)} · {c.timezone.replace(/_/g, " ")}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={c.active}
                      disabled={!canManage || c.is_main || update.isPending}
                      onCheckedChange={(next) => setActive(c, next)}
                      aria-label={`${c.name} active`}
                    />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${c.name}`}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => navigate(`/calendars/settings/${c.id}`)}>
                          <Settings2 />
                          {canManage ? "Edit calendar" : "View calendar"}
                        </DropdownMenuItem>
                        {canManage && !c.is_main && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem destructive onSelect={() => setDeleting(c)}>
                              <Trash2 />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this calendar?"
        description={
          <>
            <span className="font-medium">{deleting?.name}</span> is deleted with its staff, their hours and its blocked-off
            time. This cannot be undone. A calendar with appointments cannot be deleted: switch it off instead, so its
            appointments keep their history.
          </>
        }
        confirmLabel="Delete calendar"
        cancelLabel="Keep it"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleting && confirmDelete(deleting)}
      />
    </>
  );
}

/** Up to four faces, then "+n"; "Nobody yet" for a calendar no one can be booked on. */
function StaffStack({ calendar }: { calendar: Calendar }) {
  const staff = calendar.staff;
  if (staff.length === 0) return <span className="text-xs text-warning-11">Nobody yet</span>;
  return (
    <span className="inline-flex items-center" title={staff.map(staffName).join(", ")}>
      {staff.slice(0, 4).map((s) => (
        <Avatar key={s.user_id} name={s.display_name} email={s.email} className="-ml-1.5 ring-2 ring-surface first:ml-0" />
      ))}
      {staff.length > 4 && <span className="ml-1.5 text-xs text-text-muted">+{staff.length - 4}</span>}
    </span>
  );
}
